import { useMemo, useState } from "react";
import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import { useHeartRateStore } from "../stores/useHeartRateStore.js";
import { trekkinBubbleStyle } from "../styles/TrekkinPanelStyles.js";
import { useCan } from "../hooks/useCan.js";

const sourceLabel = (source, deviceName) => {
  if (deviceName) return deviceName;
  if (source === "airpods") return "AirPods Pro 3";
  if (source === "apple-watch") return "Apple Watch";
  if (source === "garmin") return "Garmin";
  if (source === "bluetooth") return "Sensor Bluetooth";
  return "A procurar sensor";
};

export default function HeartRateMapHUD({ embedded = false }) {
  const canUseHeartRate = useCan("PanelNav", "heart_rate_sensors");
  const isRecording = usePanelsStore((state) => state.isRecording);
  const isPaused = usePanelsStore((state) => state.isRecordingPaused);
  const bpm = useHeartRateStore((state) => state.bpm);
  const source = useHeartRateStore((state) => state.source);
  const deviceName = useHeartRateStore((state) => state.deviceName);
  const samples = useHeartRateStore((state) => state.samples);
  const [expanded, setExpanded] = useState(false);

  const metrics = useMemo(() => {
    const recent = (samples || []).slice(-60);
    const values = recent.map((sample) => Number(sample?.bpm)).filter(Number.isFinite);
    const allValues = (samples || []).map((sample) => Number(sample?.bpm)).filter(Number.isFinite);
    const width = 126;
    const height = 34;
    let path = "";
    if (values.length > 1) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = Math.max(10, max - min);
      path = values.map((value, index) => {
        const x = (index / (values.length - 1)) * width;
        const y = height - 3 - ((value - min) / range) * (height - 6);
        return `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(" ");
    }
    return {
      path,
      min: allValues.length ? Math.min(...allValues) : null,
      max: allValues.length ? Math.max(...allValues) : null,
      average: allValues.length
        ? Math.round(allValues.reduce((sum, value) => sum + value, 0) / allValues.length)
        : null,
    };
  }, [samples]);

  if (!isRecording || !canUseHeartRate) return null;

  return (
    <button
      type="button"
      aria-label="Frequência cardíaca durante a gravação"
      onClick={() => setExpanded((value) => !value)}
      style={{
        ...(embedded ? trekkinBubbleStyle : {}),
        position: embedded ? "relative" : "fixed",
        top: embedded ? undefined : "calc(env(safe-area-inset-top, 0px) + 18px)",
        right: embedded ? undefined : "calc(env(safe-area-inset-right, 0px) + 14px)",
        zIndex: embedded ? undefined : 6100,
        minWidth: embedded ? 52 : expanded ? 164 : 112,
        width: embedded ? 52 : undefined,
        height: embedded ? 52 : undefined,
        border: embedded ? trekkinBubbleStyle.border : "1px solid rgba(255,255,255,0.42)",
        borderRadius: embedded ? trekkinBubbleStyle.borderRadius : 16,
        padding: embedded ? "4px 2px 3px" : "8px 10px",
        background: embedded ? trekkinBubbleStyle.background : "rgba(20, 24, 31, 0.88)",
        color: embedded ? "#111" : "#fff",
        boxShadow: embedded ? trekkinBubbleStyle.boxShadow : "0 8px 28px rgba(0,0,0,0.28)",
        textAlign: embedded ? "center" : "left",
        pointerEvents: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: embedded ? 0 : 7 }}>
        <span
          aria-hidden="true"
          style={{
            color: bpm && !isPaused ? "#ff3b4f" : "#94a3b8",
            fontSize: embedded ? 24 : 21,
            lineHeight: 1,
            filter: bpm && !isPaused ? "drop-shadow(0 0 5px rgba(255,59,79,.6))" : "none",
          }}
        >
          ♥
        </span>
        {!embedded && <strong style={{ fontSize: 23, lineHeight: 1 }}>{bpm || "--"}</strong>}
        {!embedded && <span style={{ fontSize: 9, color: "#cbd5e1", alignSelf: "flex-end", marginBottom: 2 }}>BPM</span>}
      </div>
      {!embedded && <div
        style={{
          maxWidth: expanded ? 145 : 90,
          marginTop: 4,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 9,
          color: "#cbd5e1",
        }}
      >
        {isPaused ? "Em pausa" : sourceLabel(source, deviceName)}
      </div>}
      {embedded && (
        <div style={{ fontSize: 9, lineHeight: 1, fontWeight: 800, marginTop: 1 }}>
          {bpm ? `${bpm} BPM` : "-- BPM"}
        </div>
      )}
      {expanded && !embedded && (
        <>
          <svg viewBox="0 0 126 34" width="126" height="34" style={{ display: "block", marginTop: 6 }}>
            <path d="M0,31 L126,31" stroke="rgba(255,255,255,.16)" strokeWidth="1" />
            {metrics.path && <path d={metrics.path} fill="none" stroke="#ff5263" strokeWidth="2.2" />}
          </svg>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#cbd5e1" }}>
            <span>Mín {metrics.min ?? "--"}</span>
            <span>Méd {metrics.average ?? "--"}</span>
            <span>Máx {metrics.max ?? "--"}</span>
          </div>
        </>
      )}
    </button>
  );
}
